import { useCallback, useState } from "react";

/**
 * Which containers a picker has selected.
 *
 * Both pickers select the same way — a row at a time, or a whole waste stream
 * at once from its heading — so they share the state rather than each keeping
 * its own copy of the same two updaters.
 */
export function useContainerSelection() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Add or remove a batch of ids at once — the master and per-stream checkboxes
  // select or clear their whole scope through this.
  const setSelection = useCallback((ids: string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  }, []);

  /** Start over — e.g. the picker switched to a different facility. */
  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return { selectedIds, toggle, setSelection, clear };
}
